import type {
  HhDataItem,
  SiteDemandView,
} from "@inrange/building-manager-api-client";
import { useDemandData } from "@inrange/building-manager-api-client";
import * as echarts from "echarts";
import ReactECharts from "echarts-for-react";
import React, { useMemo, useState } from "react";
import { Button, ButtonGroup, Col, Form, Row } from "react-bootstrap";
import DateRangePicker from "../../components/DatePickers/DateRangePicker";
import CollapsibleCard from "../site/CollapsibleCard";
import { formatNumber0dp } from "../site/siteSections/utils";
import { HhDataTempFile } from "./DemandFileSummaryRow";

interface DemandDataPoint {
  x: string; // ISO timestamp
  demand: number;
}

// Columnar format returned by backend
interface DemandDataColumnar {
  x: string[];
  demand: number[];
}

interface SourceSpan {
  start: number; // inclusive index in 17,568-point array
  end: number; // exclusive index
  type:
    | "CustomerData"
    | "FillGap"
    | "FillBefore"
    | "FillAfter"
    | "AdjacentCustomerData";
  localDateStart: string; // naive local datetime
  sourceDateStart?: string; // local datetime of source row (fills only)
}

interface FullRangeData {
  startUtc: string;
  consumption: number[];
  sourceSpans: SourceSpan[];
}

// Backend response structure
interface FileDataResponse {
  fileGuid: string;
  source: "temporary" | "permanent";
  metadata: {
    date_of_analysis?: string;
    combined_df_gaps?: any[];
    latest_full_year_num_rows?: number;
    latest_full_year_num_rows_customer_data?: number;
    typical_year_start?: string;
    typical_year_end?: string;
    typical_year_data_sum?: number;
    input_file?: string;
    site_mapping?: any;
    largest_gap_days?: number;
    fullyear_data_start?: string;
    fullyear_data_end?: string;
  };
  data: DemandDataColumnar;
  sourceSpans?: SourceSpan[];
  fullRange?: FullRangeData;
}

interface DemandDataResponse {
  data: FileDataResponse[];
}

// Helper function to convert columnar format to row format
const convertDemandDataColumnarToRows = (
  columnar: DemandDataColumnar
): DemandDataPoint[] => {
  const length = columnar.x.length;
  const rows: DemandDataPoint[] = [];

  for (let i = 0; i < length; i++) {
    rows.push({
      x: columnar.x[i],
      demand: columnar.demand[i],
    });
  }

  return rows;
};

// Preset time periods for 2024
const timePresets = [
  {
    key: "full-year",
    label: "Full Year",
    start: "2024-01-01",
    end: "2024-12-31",
  },
  { key: "jan", label: "January", start: "2024-01-01", end: "2024-01-31" },
  { key: "feb", label: "February", start: "2024-02-01", end: "2024-02-29" },
  { key: "mar", label: "March", start: "2024-03-01", end: "2024-03-31" },
  { key: "apr", label: "April", start: "2024-04-01", end: "2024-04-30" },
  { key: "may", label: "May", start: "2024-05-01", end: "2024-05-31" },
  { key: "jun", label: "June", start: "2024-06-01", end: "2024-06-30" },
  { key: "jul", label: "July", start: "2024-07-01", end: "2024-07-31" },
  { key: "aug", label: "August", start: "2024-08-01", end: "2024-08-31" },
  { key: "sep", label: "September", start: "2024-09-01", end: "2024-09-30" },
  { key: "oct", label: "October", start: "2024-10-01", end: "2024-10-31" },
  { key: "nov", label: "November", start: "2024-11-01", end: "2024-11-30" },
  { key: "dec", label: "December", start: "2024-12-01", end: "2024-12-31" },
  {
    key: "feb-14",
    label: "First 14 days of Feb",
    start: "2024-02-01",
    end: "2024-02-14",
  },
  {
    key: "aug-14",
    label: "First 14 days of Aug",
    start: "2024-08-01",
    end: "2024-08-14",
  },
];

const colors = [
  "#1f3c88", // Blue
  "#d35400", // Orange
  "#27ae60", // Green
  "#8e44ad", // Purple
  "#c0392b", // Red
  "#16a085", // Teal
  "#f39c12", // Yellow
  "#2c3e50", // Dark blue
  "#e74c3c", // Light red
  "#3498db", // Light blue
];

type AggregationType = "half-hourly" | "daily" | "weekly" | "monthly";

// Returns the ISO date string (YYYY-MM-DD) of the Monday starting the week
// that contains the given date, computed in UTC to match the daily bucketing.
const weekStartKey = (date: Date): string => {
  const day = date.getUTCDay(); // 0 = Sunday .. 6 = Saturday
  const daysSinceMonday = (day + 6) % 7;
  const monday = new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate() - daysSinceMonday
    )
  );
  return monday.toISOString().split("T")[0];
};

// Helper function to aggregate data by day, week or month
const aggregateData = (
  data: DemandDataPoint[],
  aggregationType: AggregationType
): DemandDataPoint[] => {
  if (aggregationType === "half-hourly") {
    return data;
  }

  const aggregated = new Map<string, DemandDataPoint>();

  data.forEach((point) => {
    const date = new Date(point.x);
    let key: string;

    if (aggregationType === "daily") {
      key = date.toISOString().split("T")[0];
    } else if (aggregationType === "weekly") {
      key = weekStartKey(date);
    } else {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      key = `${year}-${month}`;
    }

    if (!aggregated.has(key)) {
      aggregated.set(key, { x: key, demand: 0 });
    }

    const agg = aggregated.get(key)!;
    agg.demand += point.demand || 0;
  });

  return Array.from(aggregated.values()).sort((a, b) => a.x.localeCompare(b.x));
};

const GraphDemandData = ({
  selectedTempFiles,
  selectedHhDataItems,
  siteMap,
  onClearSelection,
  autoEnable,
  includeFullRange,
}: {
  selectedTempFiles: HhDataTempFile[];
  selectedHhDataItems: HhDataItem[];
  siteMap: Map<string, SiteDemandView>;
  onClearSelection: () => void;
  autoEnable?: boolean;
  includeFullRange?: boolean;
}) => {
  const { fetchHhDataChartData } = useDemandData();

  const [startDate, setStartDate] = useState<string>("2024-01-01");
  const [endDate, setEndDate] = useState<string>("2024-12-31");
  const [timePeriod, setTimePeriod] = useState<string>(
    "full-year|2024-01-01|2024-12-31"
  );
  const [shouldFetch, setShouldFetch] = useState(autoEnable ?? false);
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});
  const [aggregationType, setAggregationType] =
    useState<AggregationType>("half-hourly");
  const [viewMode, setViewMode] = useState<"normalised" | "allData">(
    "normalised"
  );
  const [fullRangeDateRange, setFullRangeDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({ start: null, end: null });

  const isLoading = fetchHhDataChartData.isLoading;
  const error = fetchHhDataChartData.error;

  // Fetch chart data when enabled
  React.useEffect(() => {
    if (shouldFetch) {
      const MAX_FILES = 50;
      // Skip temp files that haven't been processed yet - they have no normalised-year output
      // to graph, and asking the BE to chart them errors the whole request.
      const tempFileGuids = selectedTempFiles
        .filter((f) => !!f.outputMetaData)
        .map((f) => f.fileGuid);
      const fileGuids = selectedHhDataItems.map((item) => item.guid);

      // Limit to maximum of 50 files
      const limitedTempFileGuids = tempFileGuids.slice(
        0,
        Math.min(MAX_FILES, tempFileGuids.length)
      );
      const remainingSlots = MAX_FILES - limitedTempFileGuids.length;
      const limitedFileGuids = fileGuids.slice(0, Math.max(0, remainingSlots));

      if (limitedTempFileGuids.length > 0 || limitedFileGuids.length > 0) {
        fetchHhDataChartData.mutate({
          tempFileGuids:
            limitedTempFileGuids.length > 0 ? limitedTempFileGuids : undefined,
          fileGuids: limitedFileGuids.length > 0 ? limitedFileGuids : undefined,
          includeFullRange: includeFullRange || undefined,
        });
      }
    }
    // Don't include fetchHhDataChartData in dependency array to avoid infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFetch, selectedTempFiles, selectedHhDataItems, includeFullRange]);

  // Check if any file has full range data
  const hasFullRangeData = useMemo(() => {
    if (!fetchHhDataChartData.data) return false;
    const response = fetchHhDataChartData.data as DemandDataResponse;
    return response.data?.some((f) => f.fullRange) ?? false;
  }, [fetchHhDataChartData.data]);

  // Compute full range date boundaries for the DateRangePicker
  const fullRangeBounds = useMemo(() => {
    if (!fetchHhDataChartData.data) return null;
    const response = fetchHhDataChartData.data as DemandDataResponse;
    const HH_MS = 30 * 60 * 1000;
    let minMs = Infinity;
    let maxMs = -Infinity;
    for (const fileData of response.data ?? []) {
      if (fileData.fullRange) {
        const startMs = new Date(fileData.fullRange.startUtc).getTime();
        const endMs =
          startMs + (fileData.fullRange.consumption.length - 1) * HH_MS;
        minMs = Math.min(minMs, startMs);
        maxMs = Math.max(maxMs, endMs);
      }
    }
    if (minMs === Infinity) return null;
    return { min: new Date(minMs), max: new Date(maxMs) };
  }, [fetchHhDataChartData.data]);

  // Initialise/reset full range date picker when bounds change
  React.useEffect(() => {
    if (fullRangeBounds) {
      setFullRangeDateRange({
        start: fullRangeBounds.min,
        end: fullRangeBounds.max,
      });
    }
  }, [fullRangeBounds]);

  // Process chart data
  const chartData = useMemo(() => {
    if (!fetchHhDataChartData.data) return [];

    const response = fetchHhDataChartData.data as DemandDataResponse;
    const HH_MS = 30 * 60 * 1000;
    const series: {
      id: string;
      name: string;
      color: string;
      data: { x: Date; y: number }[];
      sourceSpans?: SourceSpan[];
      epochMs?: number;
      isTemp: boolean;
    }[] = [];

    // Create a series for each file
    response.data?.forEach((fileData, index) => {
      let rowData: DemandDataPoint[];
      let seriesSourceSpans: SourceSpan[] | undefined;
      let seriesEpochMs: number | undefined;

      if (viewMode === "allData" && fileData.fullRange) {
        // Build rows from full range data
        const epochMs = new Date(fileData.fullRange.startUtc).getTime();
        seriesEpochMs = epochMs;
        rowData = fileData.fullRange.consumption.map((kwh, i) => ({
          x: new Date(epochMs + i * HH_MS).toISOString(),
          demand: kwh,
        }));
        seriesSourceSpans = fileData.fullRange.sourceSpans;
      } else {
        // Normalised year
        rowData = convertDemandDataColumnarToRows(fileData.data);
        seriesSourceSpans = fileData.sourceSpans;
      }

      // Filter by date range
      const filterStart =
        viewMode === "allData" && fullRangeDateRange.start
          ? fullRangeDateRange.start.toISOString().split("T")[0]
          : startDate;
      const filterEnd =
        viewMode === "allData" && fullRangeDateRange.end
          ? fullRangeDateRange.end.toISOString().split("T")[0]
          : endDate;

      const filteredData = rowData.filter((point) => {
        if (!filterStart || !filterEnd) return true;
        const pointDate = new Date(point.x).toISOString().split("T")[0];
        return pointDate >= filterStart && pointDate <= filterEnd;
      });

      // Apply aggregation
      const aggregatedData = aggregateData(filteredData, aggregationType);

      const total = aggregatedData.reduce(
        (sum, point) => sum + (point.demand || 0),
        0
      );

      // Find the actual filename from selected files
      const isTemp = fileData.source === "temporary";
      let fileName = fileData.fileGuid;
      let siteName = "";

      if (isTemp) {
        const tempFile = selectedTempFiles.find(
          (f) => f.fileGuid === fileData.fileGuid
        );
        fileName = tempFile?.fileName || fileData.fileGuid;
      } else {
        const hhDataItem = selectedHhDataItems.find(
          (item) => item.guid === fileData.fileGuid
        );
        fileName = hhDataItem?.fileName || fileData.fileGuid;

        // Get the first linked site name if available
        if (hhDataItem && hhDataItem.siteMappings.length > 0) {
          const firstSiteId = hhDataItem.siteMappings[0].site.id;
          const site = siteMap.get(firstSiteId);
          if (site) {
            siteName = site.name;
          }
        }
      }

      // Build the series name
      let seriesName = fileName;
      if (siteName) {
        seriesName = `${siteName} - ${fileName}`;
      }

      series.push({
        id: fileData.fileGuid,
        name: `${seriesName} (${formatNumber0dp(total)} kWh)`,
        color: colors[index % colors.length],
        data: aggregatedData.map((point) => ({
          x: new Date(point.x),
          y: point.demand || 0,
        })),
        sourceSpans: seriesSourceSpans,
        epochMs: seriesEpochMs,
        isTemp,
      });
    });

    return series;
  }, [
    fetchHhDataChartData.data,
    aggregationType,
    startDate,
    endDate,
    viewMode,
    fullRangeDateRange,
    selectedTempFiles,
    selectedHhDataItems,
    siteMap,
  ]);

  const handleTimePeriodChange = (period: string) => {
    setTimePeriod(period);

    if (period.includes("|")) {
      const [_periodKey, startDate, endDate] = period.split("|");
      setStartDate(startDate);
      setEndDate(endDate);
    }
  };

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    const matchingPreset = timePresets.find(
      (preset) => preset.start === newStartDate && preset.end === newEndDate
    );

    setTimePeriod(
      matchingPreset
        ? `${matchingPreset.key}|${matchingPreset.start}|${matchingPreset.end}`
        : "custom"
    );
  };

  const toggleSeries = (seriesId: string) => {
    setHiddenSeries((prev) => ({
      ...prev,
      [seriesId]: !prev[seriesId],
    }));
  };

  const ChartComponent = ({ height = "400px" }: { height?: string }) => {
    const filteredChartData = chartData.filter(
      (series) => !hiddenSeries[series.id]
    );

    // The fill-shading + wrap-point line annotate ONE file's source spans. They're only
    // meaningful when a single series is shown - overlaying one file's fill regions on a
    // multi-file chart is misleading. Suppress both annotations unless exactly one is visible.
    const singleSeries = filteredChartData.length === 1;

    // Compute filled-data background regions from source spans
    // Use the first series that has sourceSpans
    const spanSeriesIndex = singleSeries
      ? filteredChartData.findIndex(
          (s) => s.sourceSpans && s.sourceSpans.length > 0
        )
      : -1;
    const EPOCH_2024 = Date.UTC(2024, 0, 1, 0, 0, 0); // 2024-01-01T00:00Z
    const HH_MS = 30 * 60 * 1000; // 30 minutes in ms
    // Use the series epoch if available (allData mode), otherwise 2024 epoch
    const spanEpochMs =
      spanSeriesIndex >= 0 && filteredChartData[spanSeriesIndex].epochMs
        ? filteredChartData[spanSeriesIndex].epochMs!
        : EPOCH_2024;
    const filledAreas: { xAxis: number }[][] = [];
    if (spanSeriesIndex >= 0) {
      const spans = filteredChartData[spanSeriesIndex].sourceSpans!;
      for (const span of spans) {
        if (span.type === "CustomerData") continue;
        const startMs = spanEpochMs + span.start * HH_MS;
        const endMs = spanEpochMs + span.end * HH_MS;
        filledAreas.push([{ xAxis: startMs }, { xAxis: endMs }]);
      }
    }

    // Compute wrap-around point for normalised year view:
    // Walk spans in order and find the largest backwards jump in source
    // date — that's the year wrap, not just a gap boundary.
    let wrapPointMs: number | null = null;
    if (viewMode === "normalised" && spanSeriesIndex >= 0) {
      const spans = filteredChartData[spanSeriesIndex].sourceSpans!;
      const getSourceDate = (span: SourceSpan) => {
        const dateStr =
          span.type !== "CustomerData" && span.sourceDateStart
            ? span.sourceDateStart
            : span.localDateStart;
        return new Date(dateStr.replace(" ", "T"));
      };
      let largestJump = 0;
      for (let i = 1; i < spans.length; i++) {
        const prevMs = getSourceDate(spans[i - 1]).getTime();
        const currMs = getSourceDate(spans[i]).getTime();
        const jump = prevMs - currMs;
        if (jump > largestJump) {
          largestJump = jump;
          wrapPointMs = EPOCH_2024 + spans[i].start * HH_MS;
        }
      }
    }

    const echartsOptions = {
      animation: false,
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
        },
        formatter: (params: any) => {
          if (!params || params.length === 0) return "";

          const date = new Date(params[0].axisValue);
          let dateLabel = "";
          if (aggregationType === "monthly") {
            dateLabel = echarts.time.format(
              date.getTime(),
              "{MMM} {yyyy}",
              false
            );
          } else if (aggregationType === "weekly") {
            dateLabel = `Week of ${echarts.time.format(
              date.getTime(),
              "{dd} {MMM} {yyyy}",
              false
            )}`;
          } else if (aggregationType === "daily") {
            dateLabel = echarts.time.format(
              date.getTime(),
              "{dd} {MMM} {yyyy}",
              false
            );
          } else {
            dateLabel =
              date.toLocaleDateString() +
              " " +
              date.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              });
          }
          let tooltip = `<strong>${dateLabel}</strong><br/>`;

          params.forEach((param: any) => {
            tooltip += `${param.marker} ${param.seriesName}: ${param.value[1]?.toFixed(2) || 0} kWh<br/>`;
          });

          // Add source span info from the first series with spans
          if (spanSeriesIndex >= 0 && aggregationType === "half-hourly") {
            const spans = filteredChartData[spanSeriesIndex].sourceSpans!;
            const pointMs = date.getTime();
            const pointIndex = Math.round((pointMs - spanEpochMs) / HH_MS);
            // Binary search for the span containing this index
            let lo = 0;
            let hi = spans.length - 1;
            let span: SourceSpan | null = null;
            while (lo <= hi) {
              const mid = (lo + hi) >> 1;
              if (pointIndex < spans[mid].start) {
                hi = mid - 1;
              } else if (pointIndex >= spans[mid].end) {
                lo = mid + 1;
              } else {
                span = spans[mid];
                break;
              }
            }
            if (span) {
              const offset = pointIndex - span.start;
              const localStart = new Date(
                span.localDateStart.replace(" ", "T")
              );
              const localDate = new Date(localStart.getTime() + offset * HH_MS);
              const localStr = localDate.toLocaleString([], {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });

              let srcStr: string | null = null;
              if (span.type !== "CustomerData" && span.sourceDateStart) {
                const srcStart = new Date(
                  span.sourceDateStart.replace(" ", "T")
                );
                const srcDate = new Date(srcStart.getTime() + offset * HH_MS);
                srcStr = srcDate.toLocaleString([], {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
              }

              const spanHalfHours = span.end - span.start;
              const spanDays = spanHalfHours / 48;
              const durationStr =
                spanDays >= 1
                  ? `${Math.round(spanDays)} day${Math.round(spanDays) !== 1 ? "s" : ""}`
                  : `${Math.round(spanHalfHours / 2)} hour${Math.round(spanHalfHours / 2) !== 1 ? "s" : ""}`;

              if (span.type === "CustomerData") {
                tooltip += `<br/><em>Customer data from ${localStr}</em>`;
              } else if (span.type === "FillGap") {
                tooltip += `<br/><em>Gap fill (${durationStr}) — copied from ${srcStr}</em>`;
              } else if (span.type === "AdjacentCustomerData") {
                tooltip += `<br/><em>Adjacent value — copied from ${srcStr}</em>`;
              } else if (
                span.type === "FillBefore" ||
                span.type === "FillAfter"
              ) {
                tooltip += `<br/><em>Extended (${durationStr}) — copied from ${srcStr}</em>`;
              }
            }
          }

          return tooltip;
        },
      },
      legend: {
        show: false,
      },
      grid: {
        left: "20px",
        right: "40px",
        bottom: "40px",
        top: "40px",
        containLabel: true,
      },
      xAxis: {
        type: "time",
        axisLabel: {
          formatter: (value: number) => {
            if (aggregationType === "monthly") {
              return echarts.time.format(value, "{MMM} {yyyy}", false);
            } else {
              return echarts.time.format(value, "{MMM} {dd}", false);
            }
          },
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        name: "Demand (kWh)",
        position: "left",
        axisLabel: {
          formatter: "{value}",
        },
        splitLine: {
          show: false,
        },
      },
      series: filteredChartData.map((series, index) => {
        const seriesData = series.data.map((point) => [
          point.x.getTime(),
          point.y,
        ]);

        const seriesConfig: any = {
          name: series.name,
          type: "line",
          data: seriesData,
          itemStyle: {
            color: series.color,
          },
          lineStyle: {
            color: series.color,
            width: 2,
            type: "solid",
          },
          symbol: "none",
          emphasis: {
            focus: "series",
          },
        };

        // Add filled-data background shading to the first series with source spans
        if (index === spanSeriesIndex && filledAreas.length > 0) {
          seriesConfig.markArea = {
            silent: true,
            itemStyle: {
              color: "rgba(200, 200, 200, 0.3)",
            },
            data: filledAreas,
          };
        }

        // Add wrap-around line on the first series in normalised view
        if (index === 0 && wrapPointMs !== null) {
          seriesConfig.markLine = {
            silent: true,
            symbol: "none",
            lineStyle: {
              color: "#000000",
              width: 2,
              type: "solid",
            },
            label: {
              show: true,
              formatter: "Wrap point",
              position: "insideStartTop",
              fontSize: 11,
            },
            data: [{ xAxis: wrapPointMs }],
          };
        }

        return seriesConfig;
      }),
    };

    return (
      <div style={{ height, width: "100%" }}>
        <ReactECharts
          option={echartsOptions}
          style={{ height: "100%", width: "100%" }}
          opts={{ renderer: "canvas" }}
        />
      </div>
    );
  };

  const LegendComponent = () => (
    <div
      className="mt-3 mb-3 d-flex justify-left gap-2 flex-wrap"
      style={{ fontSize: "12px" }}
    >
      {chartData.map((series) => (
        <div
          key={series.id}
          className={`${!hiddenSeries[series.id] ? "" : "text-muted"}`}
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <div
            onClick={() => toggleSeries(series.id)}
            style={{
              cursor: "pointer",
              width: "20px",
              height: "12px",
              backgroundColor: hiddenSeries[series.id] ? "#ccc" : series.color,
              marginRight: "5px",
              border: "1px solid #999",
            }}
          />
          <span
            onClick={() => toggleSeries(series.id)}
            style={{
              cursor: "pointer",
              textDecoration: hiddenSeries[series.id] ? "line-through" : "none",
            }}
          >
            {series.name} {series.isTemp ? "(temp)" : ""}
          </span>
        </div>
      ))}
    </div>
  );

  const TimeControls = ({ idPrefix = "" }: { idPrefix?: string }) => (
    <>
      {hasFullRangeData && (
        <Col md="auto" className="mb-3">
          <Form.Label>View</Form.Label>
          <div>
            <ButtonGroup size="sm">
              <Button
                variant={
                  viewMode === "normalised" ? "primary" : "outline-primary"
                }
                onClick={() => setViewMode("normalised")}
              >
                Normalised year
              </Button>
              <Button
                variant={viewMode === "allData" ? "primary" : "outline-primary"}
                onClick={() => setViewMode("allData")}
              >
                All data
                {fullRangeBounds &&
                  ` (${fullRangeBounds.min.toISOString().slice(0, 10)} – ${fullRangeBounds.max.toISOString().slice(0, 10)})`}
              </Button>
            </ButtonGroup>
          </div>
        </Col>
      )}
      {viewMode === "allData" && fullRangeBounds ? (
        <>
          <Col md={5} className="mb-3">
            <Form.Label>Date Range</Form.Label>
            <DateRangePicker
              value={fullRangeDateRange}
              onChange={setFullRangeDateRange}
              mode="date"
              minDate={fullRangeBounds.min}
              maxDate={fullRangeBounds.max}
            />
          </Col>
        </>
      ) : (
        <>
          <Col md={3} className="mb-3">
            <Form.Group controlId={`${idPrefix}timePeriod`}>
              <Form.Label>Time Period</Form.Label>
              <Form.Select
                value={timePeriod}
                onChange={(e) => handleTimePeriodChange(e.target.value)}
              >
                {timePresets.map((preset) => (
                  <option
                    key={preset.key}
                    value={`${preset.key}|${preset.start}|${preset.end}`}
                  >
                    {preset.label}
                  </option>
                ))}
                <option value="custom">Custom</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={2} className="mb-3">
            <Form.Group controlId={`${idPrefix}startDate`}>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => handleDateChange(e.target.value, endDate)}
                min="2024-01-01"
                max="2024-12-31"
              />
            </Form.Group>
          </Col>
          <Col md={2} className="mb-3">
            <Form.Group controlId={`${idPrefix}endDate`}>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => handleDateChange(startDate, e.target.value)}
                min="2024-01-01"
                max="2024-12-31"
              />
            </Form.Group>
          </Col>
        </>
      )}
      <Col md={2} className="mb-3">
        <Form.Group controlId={`${idPrefix}aggregation`}>
          <Form.Label>Aggregation</Form.Label>
          <Form.Select
            value={aggregationType}
            onChange={(e) =>
              setAggregationType(e.target.value as AggregationType)
            }
          >
            <option value="half-hourly">Half-Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </Form.Select>
        </Form.Group>
      </Col>
    </>
  );

  // Compute per-calendar-year summary from fullRange data
  const yearSummaries = useMemo(() => {
    if (!fetchHhDataChartData.data) return null;
    const response = fetchHhDataChartData.data as DemandDataResponse;
    // Use the first file that has fullRange data
    const fileData = response.data?.find((f) => f.fullRange);
    if (!fileData?.fullRange) return null;

    const { startUtc, consumption } = fileData.fullRange;
    const HH_MS = 30 * 60 * 1000;
    const startMs = new Date(startUtc).getTime();

    // Group consumption by calendar year
    const yearData: Map<
      number,
      { total: number; firstMs: number; lastMs: number }
    > = new Map();
    for (let i = 0; i < consumption.length; i++) {
      const ms = startMs + i * HH_MS;
      const year = new Date(ms).getFullYear();
      const entry = yearData.get(year);
      if (entry) {
        entry.total += consumption[i];
        entry.lastMs = ms;
      } else {
        yearData.set(year, {
          total: consumption[i],
          firstMs: ms,
          lastMs: ms,
        });
      }
    }

    // Compute scaled annual totals
    const summaries: {
      year: number;
      scaledTotal: number;
      rangeLabel: string;
    }[] = [];
    for (const [year, data] of Array.from(yearData.entries()).sort(
      (a, b) => a[0] - b[0]
    )) {
      const yearStartMs = new Date(year, 0, 1).getTime();
      const yearEndMs = new Date(year + 1, 0, 1).getTime();
      const totalYearMs = yearEndMs - yearStartMs;
      // Coverage: from first data point to last data point + 30min
      const coveredMs = data.lastMs + HH_MS - data.firstMs;
      const fraction = coveredMs / totalYearMs;
      const scaledTotal = data.total / fraction;

      const firstDate = new Date(data.firstMs);
      const lastDate = new Date(data.lastMs);
      const fmtDate = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

      const pctOfYear = Math.round(fraction * 100);
      const rangeLabel =
        fraction < 0.999
          ? `  - based on ${pctOfYear}% of full year (${fmtDate(firstDate)} - ${fmtDate(lastDate)}) linearly scaled up to a full year`
          : "";

      summaries.push({ year, scaledTotal, rangeLabel });
    }

    return summaries;
  }, [fetchHhDataChartData.data]);

  // Compute quality info from metadata
  const qualityInfo = useMemo(() => {
    if (!fetchHhDataChartData.data) return null;
    const response = fetchHhDataChartData.data as DemandDataResponse;
    const fileData = response.data?.[0];
    if (!fileData?.metadata) return null;

    const {
      latest_full_year_num_rows_customer_data,
      latest_full_year_num_rows,
      largest_gap_days,
    } = fileData.metadata;
    if (!latest_full_year_num_rows_customer_data || !latest_full_year_num_rows)
      return null;

    const pct = Math.round(
      (latest_full_year_num_rows_customer_data / latest_full_year_num_rows) *
        100
    );
    const belowThreshold = pct < 85;
    const gapDays =
      largest_gap_days !== undefined ? Math.round(largest_gap_days) : undefined;
    const largeGap = gapDays !== undefined && gapDays > 14;
    const isWarning = belowThreshold || largeGap;

    let message = `Demand data: Normalised year ${pct}% directly from customer data`;
    if (belowThreshold) {
      message += ` (<85% threshold)`;
    }
    if (gapDays !== undefined) {
      message += `, largest gap ${gapDays} days`;
      if (largeGap) {
        message += ` (>14 day threshold)`;
      }
    }
    message += ".";

    return { message, isWarning };
  }, [fetchHhDataChartData.data]);

  const YearSummaryAndWarning = () => {
    if (!yearSummaries && !qualityInfo) return null;
    return (
      <div className="mt-2 mb-2" style={{ fontSize: "13px" }}>
        {yearSummaries && yearSummaries.length > 0 && (
          <div className="mb-1">
            {yearSummaries.map((s) => (
              <div key={s.year}>
                <b>{s.year}</b> - {formatNumber0dp(s.scaledTotal)} kWh
                {s.rangeLabel}
              </div>
            ))}
            {(() => {
              const response = fetchHhDataChartData.data as DemandDataResponse;
              const meta = response?.data?.[0]?.metadata;
              if (!meta?.typical_year_data_sum) return null;
              const range =
                meta.fullyear_data_start && meta.fullyear_data_end
                  ? ` - based on ${meta.fullyear_data_start} to ${meta.fullyear_data_end}`
                  : "";
              return (
                <div>
                  <b>Normalised year</b> -{" "}
                  {formatNumber0dp(meta.typical_year_data_sum)} kWh{range}
                </div>
              );
            })()}
          </div>
        )}
        {qualityInfo && (
          <div
            className={qualityInfo.isWarning ? "text-warning" : "text-success"}
          >
            <strong>
              {qualityInfo.isWarning ? "\u26A0\uFE0F" : "\u2705"}{" "}
              {qualityInfo.message}
            </strong>
          </div>
        )}
      </div>
    );
  };

  const totalSelected = selectedTempFiles.length + selectedHhDataItems.length;
  const MAX_FILES = 50;
  const exceedsLimit = totalSelected > MAX_FILES;

  if (totalSelected === 0) {
    return null;
  }

  return (
    <CollapsibleCard
      title={
        autoEnable
          ? `Demand shape`
          : `Graph Selected Demand Data (${totalSelected} file${totalSelected > 1 ? "s" : ""})`
      }
      className="mt-3"
      initialOpen={true}
    >
      {exceedsLimit && (
        <div className="alert alert-warning" role="alert">
          <strong>Warning:</strong> You have selected {totalSelected} files, but
          only the first {MAX_FILES} files will be graphed (maximum limit).
        </div>
      )}
      <Row className="d-flex flex-wrap align-items-end">
        <TimeControls />
        {!autoEnable && (
          <Col md={2} className="d-flex align-items-end gap-2 mb-3">
            <Button
              variant="primary"
              onClick={() => {
                setShouldFetch(!shouldFetch);
                if (!shouldFetch) {
                  setHiddenSeries({});
                }
              }}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : shouldFetch ? "Disable" : "Enable"}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                onClearSelection();
                setShouldFetch(false);
              }}
            >
              Clear
            </Button>
          </Col>
        )}
      </Row>

      {isLoading && (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-2">Loading chart data...</div>
        </div>
      )}

      {!!error && (
        <div className="alert alert-danger" role="alert">
          Error loading demand data:{" "}
          {String(error instanceof Error ? error.message : error)}
        </div>
      )}

      {shouldFetch && fetchHhDataChartData.data && <LegendComponent />}

      {shouldFetch && chartData.length > 0 && <ChartComponent />}

      {shouldFetch && fetchHhDataChartData.data && <YearSummaryAndWarning />}

      {shouldFetch && fetchHhDataChartData.data && chartData.length === 0 && (
        <div className="text-center py-4 text-muted">
          No data available for the selected date range.
        </div>
      )}
    </CollapsibleCard>
  );
};

export default GraphDemandData;
